import BaseApi from "./BaseApi";
import { logout } from "./AuthApi";
import { DSR_ACCESS_TOKEN } from "@/store/constants/global";
import { authState, getToken } from "@/services/AuthService";

/**
 * チャットメッセージを取得するAPI
 * @param {Object} data - クエリパラメータ
 * @returns {Promise} - APIレスポンス
 */
export function getChatMessagesApi(data) {
  return BaseApi.get("/chat/messages", {
    params: data,
  });
}

/**
 * チャットメッセージを送信するAPI
 * @param {Object} message - 送信するメッセージデータ
 * @returns {Promise} - APIレスポンス
 */
export function postChatMessageApi(message) {
  return BaseApi.post("/chat/messages", message);
}

/**
 * LLM APIを呼び出し、ストリームイベントとしてレスポンスを受け取る
 * @param {string} question - 質問文字列
 * @param {string} [chatType="freetalk"] - チャットタイプ。
 *   - "freetalk": 自由質問
 *   - "latest_diary_search": 日報から安全指示事項を検索しおすすめを提示
 * @returns {ReadableStream} - テキストストリーム
 */
export async function callLLMApi(question, chatType = "freetalk") {
  try {
    // LLM専用のベースURLとトークンを取得
    const BASE_URL = process.env.VUE_APP_LLM_API || "http://localhost:8081";
    // Auth0 IDトークンは DSR_ACCESS_TOKEN に保存されている（旧 AIARS_ACCESS_TOKEN は未使用）
    const token = localStorage.getItem(DSR_ACCESS_TOKEN);

    // fetchAPIを使用してストリームレスポンスを取得
    const response = await fetch(
      `${BASE_URL}/v1/chat/generate?chatType=${encodeURIComponent(chatType)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Token": `Bearer ${token}`,
          // 後方互換性のために従来のトークンヘッダーも維持
          token: token,
        },
        body: JSON.stringify({ question }),
      }
    );
    return response.body;
  } catch (error) {
    console.error("Error calling LLM API:", error);
    if (error.response && error.response.status === 401) {
      logout();
    }
    throw error;
  }
}

/**
 * 改行区切りJSON(NDJSON)のストリームを読み取り、行ごとにコールバックへ渡す共通処理
 * @param {ReadableStream} stream - fetchレスポンスのbody
 * @param {Function} onChunk - パース済みJSONオブジェクトを処理するコールバック
 * @param {Function} onComplete - ストリーム完了時に呼び出されるコールバック
 * @returns {Promise} - 処理完了時のPromise
 */
async function processJsonStream(stream, onChunk, onComplete) {
  if (stream && typeof stream.getReader === "function") {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let accumulatedText = "";
    let buffer = "";

    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;

      if (done) {
        if (onComplete) onComplete(accumulatedText);
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      accumulatedText += chunk;
      buffer += chunk;

      // 改行で分割し、最後の不完全な行はバッファに残す
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.trim() === "") continue; // 空行はスキップ
        try {
          const parsedData = JSON.parse(line);
          if (onChunk) onChunk(parsedData);
        } catch (e) {
          console.warn("Failed to parse line as JSON:", e, line);
        }
      }
    }
  } else if (stream) {
    // 通常のJSONレスポンスの場合
    if (onChunk) onChunk(stream);
    if (onComplete) onComplete(stream);
  }
}

/**
 * LLM APIを呼び出し、コールバック関数でテキストチャンクを処理する
 * @param {string} question - 質問文字列
 * @param {Function} onChunk - 各テキストチャンクを処理するコールバック関数
 * @param {Function} onComplete - ストリーム完了時に呼び出されるコールバック関数
 * @param {Function} onError - エラー発生時に呼び出されるコールバック関数
 * @param {string} [chatType="freetalk"] - チャットタイプ（freetalk / latest_diary_search 等）
 * @returns {Promise} - 処理完了時のPromise
 */
export async function callLLMApiWithCallback(
  question,
  onChunk,
  onComplete,
  onError,
  chatType = "freetalk"
) {
  try {
    const stream = await callLLMApi(question, chatType);
    await processJsonStream(stream, onChunk, onComplete);
  } catch (error) {
    console.error("Error processing LLM API stream:", error);
    if (onError) onError(error);
    throw error;
  }
}

/**
 * backend の RAG 中継エンドポイントを呼ぶ（#962）。
 *
 * ## なぜ backend を経由するのか
 *
 * 以前はブラウザから d-prompt-manager を直接呼び、共有キー `Ext-Api-Key` を
 * 付けていた。`VUE_APP_*` は Vue CLI が**ビルド時にバンドルへ展開する**ため、
 * このキーは DevTools を開けば誰でも取得できる — 秘密として機能していなかった。
 *
 * このアプリには身元が **2 種類**ある（`router.js` の `requiresAuth` は両方通す）:
 *
 * - **社員**: Auth0。d-prompt-manager と同じテナント・同じ audience
 * - **契約社員**: `contractor_token`（このアプリ独自のトークン）。
 *   Auth0 の身元を持たないので d-prompt-manager は検証しようがない
 *
 * 契約社員トークンを検証できるのは**このアプリの backend だけ**。だから
 * ブラウザから秘密を無くすには契約社員の経路を backend 経由にするしかない。
 * 社員も同じ経路に揃えてある（分岐を 2 本持つ理由が無い）。
 *
 * ## ヘッダーが 2 つある理由
 *
 * - `Authorization`: **backend の認証用**。他の API 呼び出しと全く同じもの
 *   （契約社員トークン、または Auth0 の ID トークン）を送る
 * - `X-Auth0-Access-Token`: **d-prompt-manager へ転送される**アクセストークン
 *
 * ⚠️ `Authorization` を転送に流用できない。あそこに入るのは
 * `idTokenClaims.__raw`（**ID トークン**）で、`aud` がクライアント ID になるため
 * d-prompt-manager の audience 検証を通らない。転送してよいのは
 * `getTokenSilently()` が返す**アクセストークン**だけ。
 *
 * アクセストークンが取れなかった社員は backend 側がサーバー資格情報で通す
 * （機能は落とさない）。ただし identity が届かなくなるので backend が
 * `PROMPT_MANAGER_RELAY_MISSING_USER_TOKEN` を警告ログに残す。
 *
 * @param {string} path - backend のパス（例: "/chat/manual-rag"）
 * @param {string} question - 質問文字列
 * @param {string} errorLabel - エラーメッセージの見出し
 * @returns {ReadableStream} - NDJSON のテキストストリーム
 */
async function callBackendRagApi(path, question, errorLabel) {
  const BASE_URL = process.env.VUE_APP_BASE_URL_API;
  const headers = { "Content-Type": "application/json" };

  const contractorToken = localStorage.getItem("contractor_token");
  if (contractorToken) {
    headers["Authorization"] = `Bearer ${contractorToken}`;
  } else {
    // BaseApi と同じ選び方（IDトークン優先、無ければ Auth0 から取得）
    const authToken = localStorage.getItem(DSR_ACCESS_TOKEN) || (await getToken());
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const accessToken = await getAuth0AccessTokenForRelay();
    if (accessToken) {
      headers["X-Auth0-Access-Token"] = accessToken;
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ question }),
  });

  if (!response.ok) {
    const detail = await readErrorDetail(response);

    if (response.status === 401) {
      // この 2 本は d-prompt-manager 直叩きから**このアプリ自身の認証**を使う経路に
      // 変わった。`BaseApi` の「401 なら logout」インターセプタは fetch を通らないので
      // ここで面倒を見る。放っておくとこの機能だけが毎回 401 で失敗し続け、
      // 利用者には汎用エラーしか出ない。
      redirectToSignIn();
    }

    const error = new Error(`${errorLabel}: ${response.status} ${detail}`);
    error.status = response.status;
    // 利用者にそのまま見せてよい理由だけを載せる。d-prompt-manager は権限不足を
    // 400 + `detail` で返す（403 だと向こうのフロントが強制ログアウトするため）。
    // 5xx の本文はサーバー内部の文言なので画面には出さない。
    error.userMessage = response.status === 400 ? detail : "";
    throw error;
  }
  return response.body;
}

/**
 * 認証が切れているときにサインイン画面へ戻す。
 * 契約社員は Auth0 ではなく専用のサインイン画面（`BaseApi` の logout と同じ扱い）。
 */
function redirectToSignIn() {
  const contractorToken = localStorage.getItem("contractor_token");
  if (contractorToken) {
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("contractor_email");
    window.location.href = "/contractor/signin";
    return;
  }
  logout();
}

/**
 * d-prompt-manager へ転送するための Auth0 アクセストークンを取得する。
 * 取れなければ null（backend がサーバー資格情報へフォールバックする）。
 * @returns {Promise<string|null>}
 */
async function getAuth0AccessTokenForRelay() {
  if (!authState.isAuthenticated || !authState.auth0Client) {
    return null;
  }
  try {
    return (await authState.auth0Client.getTokenSilently()) || null;
  } catch (error) {
    console.error("Auth0 アクセストークンの取得に失敗しました:", error);
    return null;
  }
}

/**
 * エラーレスポンスから利用者に見せる理由を取り出す。
 *
 * d-prompt-manager は権限不足のとき 400 + `{"detail": "..."}` を返す。
 * ステータスコードだけ出すと「400」としか分からず、原因に辿り着けない。
 *
 * @param {Response} response - fetch のレスポンス
 * @returns {Promise<string>} - 理由の文字列（取れなければ空文字）
 */
async function readErrorDetail(response) {
  try {
    const text = await response.text();
    if (!text) return "";
    try {
      const parsed = JSON.parse(text);
      return parsed.detail || parsed.message || text;
    } catch (e) {
      return text;
    }
  } catch (e) {
    return "";
  }
}

/**
 * 安全指示事項検索（latest-search）APIを呼び出し、ストリームレスポンスを取得する
 *
 * backend の中継エンドポイント (/chat/latest-diary-search) 経由で
 * d-prompt-manager を呼ぶ。詳細は `callBackendRagApi` を参照。
 *
 * @param {string} question - 質問文字列
 * @returns {ReadableStream} - テキストストリーム
 */
export async function callLatestDiarySearchRagApi(question) {
  return callBackendRagApi(
    "/chat/latest-diary-search",
    question,
    "Latest diary search API error"
  );
}

/**
 * 安全指示事項チャットを呼び出し、コールバックでおすすめ安全指示事項を処理する
 *
 * ユーザーが作業種類やキーワード・安全指示の文言を質問すると、過去の日報を
 * 検索しておすすめの安全指示事項(result.safetyInstructionSearchResult.items)を返す。
 *
 * @param {string} question - 質問文字列（作業種類・キーワード・安全指示の文言など）
 * @param {Function} onChunk - 各チャンクを処理するコールバック関数
 * @param {Function} onComplete - ストリーム完了時に呼び出されるコールバック関数
 * @param {Function} onError - エラー発生時に呼び出されるコールバック関数
 * @returns {Promise} - 処理完了時のPromise
 */
export async function callSafetyInstructionChatApiWithCallback(
  question,
  onChunk,
  onComplete,
  onError
) {
  try {
    const stream = await callLatestDiarySearchRagApi(question);
    await processJsonStream(stream, onChunk, onComplete);
  } catch (error) {
    console.error("Error processing latest diary search stream:", error);
    if (onError) onError(error);
    throw error;
  }
}

/**
 * マニュアル検索（manual-rag）APIを呼び出し、ストリームレスポンスを取得する
 *
 * backend の中継エンドポイント (/chat/manual-rag) 経由で d-prompt-manager を
 * 呼ぶ。詳細は `callBackendRagApi` を参照。
 *
 * @param {string} question - 質問文字列
 * @returns {ReadableStream} - テキストストリーム
 */
export async function callManualRagApi(question) {
  return callBackendRagApi(
    "/chat/manual-rag",
    question,
    "Manual RAG API error"
  );
}

/**
 * マニュアル検索を呼び出し、コールバックで回答と参照元を処理する
 *
 * 回答テキスト(result.token)に加え、参照元(result.ragRetrievalResult.items)を返す。
 *
 * @param {string} question - 質問文字列
 * @param {Function} onChunk - 各チャンクを処理するコールバック関数
 * @param {Function} onComplete - ストリーム完了時に呼び出されるコールバック関数
 * @param {Function} onError - エラー発生時に呼び出されるコールバック関数
 * @returns {Promise} - 処理完了時のPromise
 */
export async function callManualSearchApiWithCallback(
  question,
  onChunk,
  onComplete,
  onError
) {
  try {
    const stream = await callManualRagApi(question);
    await processJsonStream(stream, onChunk, onComplete);
  } catch (error) {
    console.error("Error processing manual RAG stream:", error);
    if (onError) onError(error);
    throw error;
  }
}

/**
 * チャット履歴を取得するAPI
 * @param {string} chatId - チャットID
 * @returns {Promise} - APIレスポンス
 */
export function getChatHistoryApi(chatId) {
  return BaseApi.get(`/chat/history/${chatId}`);
}
